import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Edit2, Eye, RefreshCw, AlertCircle, Download, TrendingUp, TrendingDown, DollarSign, BarChart3, Target, Calendar, Package, Activity, Trash2, X } from 'lucide-react';
import StockEditForm from './StockEditForm';
import axiosInstance from '../services/axiosInstance';
import { formatIndianDateTime } from '../utils/timeUtils';

// ShadCN UI Components
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const SectorDetailsDialog = ({ isOpen, onClose, sector }) => {
    const [stocks, setStocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);
    const [selectedStockId, setSelectedStockId] = useState(null);
    const [livePriceModal, setLivePriceModal] = useState(false);
    const [livePriceData, setLivePriceData] = useState(null);
    const [loadingPrice, setLoadingPrice] = useState(false);
    const [priceError, setPriceError] = useState(null);
    const [selectedExchange, setSelectedExchange] = useState('NSE');

    // Delete confirmation modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [stockToDelete, setStockToDelete] = useState(null);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [userInputText, setUserInputText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setStocks([]);
            setLoading(true);
            setError(null);
            return;
        }

        // Avoid firing a request with an empty sector during initial open.
        if (!sector) {
            setLoading(false);
            setError(null);
            setStocks([]);
            return;
        }

        fetchSectorStocks(sector);
    }, [isOpen, sector]);

    const fetchSectorStocks = async (sectorName = sector) => {
        if (!sectorName) return;

        try {
            setLoading(true);
            setError(null);
            const sectorParam = sectorName || "Uncategorized";
            const encodedSector = encodeURIComponent(sectorParam);

            const response = await axiosInstance.get(`/stock/sector/${encodedSector}/stocks`);
            const data = response.data;

            if (data.success) {
                setStocks(data.stocks || []);
            } else {
                throw new Error(data.message || 'Failed to fetch sector data');
            }
        } catch (error) {
            console.error('Error fetching sector stocks:', error);
            setError(error.response?.data?.message || error.message);
            setStocks([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchLivePrice = async (stockName) => {
        try {
            setLoadingPrice(true);
            setPriceError(null);

            const response = await axiosInstance.get('/live/live-price', {
                params: {
                    exchange: selectedExchange,
                    symbol: stockName
                },
                timeout: 30000
            });

            if (response.data.success) {
                setLivePriceData(response.data.data);
            } else {
                throw new Error(response.data.error || 'Failed to fetch live price');
            }
        } catch (error) {
            console.error('Error fetching live price:', error);
            let errorMessage = 'Failed to fetch live price';

            if (error.code === 'ECONNABORTED') {
                errorMessage = 'Request timed out. The server is taking too long to respond. Please try again.';
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setPriceError(errorMessage);
        } finally {
            setLoadingPrice(false);
        }
    };

    const handleEdit = (stockId) => {
        setSelectedStockId(stockId);
        setIsEditFormOpen(true);
    };

    const handleViewDetails = (stockId, stockName) => {
        setSelectedStockId(stockId);
        setLivePriceModal(true);
        fetchLivePrice(stockName);
    };

    const handleEditFormClose = () => {
        setIsEditFormOpen(false);
        setSelectedStockId(null);
    };

    const handleEditFormSave = () => {
        fetchSectorStocks();
    };

    const handleDelete = (stock) => {
        setStockToDelete(stock);
        setDeleteConfirmationText(generateRandomText());
        setUserInputText('');
        setDeleteModalOpen(true);
    };

    const generateRandomText = () => {
        const words = ['DELETE', 'REMOVE', 'PERMANENT', 'IRREVERSIBLE', 'CONFIRM', 'WARNING'];
        const randomWord = words[Math.floor(Math.random() * words.length)];
        return randomWord;
    };

    const handleConfirmDelete = async () => {
        if (userInputText !== deleteConfirmationText) {
            return;
        }

        try {
            setIsDeleting(true);
            const response = await axiosInstance.delete(`/stock/${stockToDelete.id}`);

            if (response.data.success) {
                // Remove the stock from the local state
                setStocks(prevStocks => prevStocks.filter(stock => stock.id !== stockToDelete.id));
                setDeleteModalOpen(false);
                setStockToDelete(null);
                setDeleteConfirmationText('');
                setUserInputText('');
            } else {
                throw new Error(response.data.message || 'Failed to delete stock');
            }
        } catch (error) {
            console.error('Error deleting stock:', error);
            alert(error.response?.data?.message || error.message || 'Failed to delete stock');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setStockToDelete(null);
        setDeleteConfirmationText('');
        setUserInputText('');
    };

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null || isNaN(amount)) {
            return '₹0.00';
        }
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatNumber = (number) => {
        if (number === undefined || number === null || isNaN(number)) {
            return '0';
        }
        return new Intl.NumberFormat('en-IN').format(number);
    };

    const formatPercentage = (value) => {
        if (value === undefined || value === null || value === '') {
            return '0.00%';
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            return '0.00%';
        }
        return `${numValue.toFixed(2)}%`;
    };



    const formatTransaction = (transaction) => {
        if (!transaction) return (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                <Package size={16} />
                <span>No transactions</span>
            </div>
        );

        // Format transaction date with proper IST handling
        const formatTransactionDate = (dateString) => {
            if (!dateString) return 'N/A';

            try {
                // Check if the original date string contains time information
                const hasTime = dateString.includes('T') && /\d{1,2}:\d{2}/.test(dateString.split('T')[1] || '');

                let date;

                if (hasTime) {
                    // If the date string has time but no timezone info, treat it as IST
                    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
                        // Append IST timezone offset to treat it as IST time
                        date = new Date(dateString + '+05:30');
                    } else {
                        date = new Date(dateString);
                    }
                } else {
                    // For date-only strings, create date in IST
                    date = new Date(dateString + 'T00:00:00+05:30');
                }

                if (isNaN(date.getTime())) return 'N/A';

                if (hasTime) {
                    // Show date and time if time is present
                    return date.toLocaleString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                    });
                } else {
                    // Show only date if no time is present
                    return date.toLocaleDateString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    });
                }
            } catch (error) {
                console.error('Error formatting transaction date:', error);
                return 'N/A';
            }
        };

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                <div className="flex items-center gap-2 mb-2 text-gray-600">
                    <Calendar size={14} />
                    <span className="text-sm font-medium">{formatTransactionDate(transaction.date)}</span>
                </div>
                <div className="space-y-1">
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Price:</span>
                        <span className="text-sm font-semibold text-gray-900">{formatAmount(transaction.price)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Quantity:</span>
                        <span className="text-sm font-semibold text-gray-900">{transaction.quantity}</span>
                    </div>
                </div>
            </div>
        );
    };

    const handleDownload = () => {
        const headers = [
            'Stock Name',
            'Remaining Qty',
            'Total Investment',
            'Total Returns',
            'Profit/Loss',
            'Watch Price'
        ];

        const rows = stocks.map(stock => [
            stock.stockName,
            stock.remainingQty,
            stock.totalInvestment,
            stock.totalReturns,
            stock.profitLoss,
            stock.watchDetails?.targetPrice || ''
        ]);

        const csvContent = [
            headers,
            ...rows
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${sector || 'Uncategorized'}_sector_details.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const calculateSectorStats = () => {
        if (!stocks.length) return null;

        const totalInvestment = stocks.reduce((sum, stock) => sum + (stock.totalInvestment || 0), 0);
        const totalReturns = stocks.reduce((sum, stock) => sum + (stock.totalReturns || 0), 0);
        const totalProfitLoss = stocks.reduce((sum, stock) => sum + (stock.profitLoss || 0), 0);
        const totalStocks = stocks.length;
        const stocksWithProfit = stocks.filter(stock => (stock.profitLoss || 0) > 0).length;

        return {
            totalInvestment,
            totalReturns,
            totalProfitLoss,
            totalStocks,
            stocksWithProfit,
            profitPercentage: totalInvestment > 0 ? (totalProfitLoss / totalInvestment) * 100 : 0
        };
    };

    const sectorStats = calculateSectorStats();

    // Helper function to get the appropriate CSS class for profit/loss
    const getProfitLossClass = (profitLoss) => {
        if (profitLoss === 0) return 'text-gray-600';
        return profitLoss > 0 ? 'text-green-600' : 'text-red-600';
    };

    const LivePriceModal = () => {
        if (!livePriceModal) return null;

        const currentStock = stocks.find(stock => stock.id === selectedStockId);

        const calculateCurrentDetails = () => {
            if (!currentStock || !livePriceData?.ltp) return null;

            const livePrice = parseFloat(livePriceData.ltp);
            if (isNaN(livePrice)) return null;

            if (currentStock.remainingQty > 0) {
                const avgCost = currentStock.totalInvestment / currentStock.remainingQty;
                const currentValue = currentStock.remainingQty * livePrice;
                const liveProfitLoss = currentValue - currentStock.totalInvestment;
                const liveProfitLossPercentage = (liveProfitLoss / currentStock.totalInvestment) * 100;

                return {
                    avgCost,
                    currentValue,
                    liveProfitLoss,
                    liveProfitLossPercentage: parseFloat(liveProfitLossPercentage)
                };
            }
            return null;
        };

        const currentDetails = calculateCurrentDetails();
        const change = parseFloat(livePriceData?.change || 0);
        const stockName = currentStock?.stockName || livePriceData?.symbol || livePriceData?.ts?.replace('-EQ', '') || 'Unknown Stock';

        const buyHistory = currentStock?.buyDetails || [];
        const sellHistory = currentStock?.sellDetails || [];
        const totalBought = buyHistory.reduce((sum, b) => sum + (b.quantity || 0), 0);
        const totalSold = sellHistory.reduce((sum, s) => sum + (s.quantity || 0), 0);
        const avgBuy = totalBought ? (buyHistory.reduce((sum, b) => sum + ((b.price || 0) * (b.quantity || 0)), 0) / totalBought) : 0;
        const avgSell = totalSold ? (sellHistory.reduce((sum, s) => sum + ((s.price || 0) * (s.quantity || 0)), 0) / totalSold) : 0;
        const remainingQty = currentStock?.remainingQty || 0;
        const totalInvestment = currentStock?.totalInvestment || 0;
        const totalReturns = currentStock?.totalReturns || 0;
        const realizedPL = totalReturns - (totalSold * avgBuy);
        const watchPrice = currentStock?.watchDetails?.targetPrice;
        const livePrice = parseFloat(livePriceData?.ltp || 0);
        const currentValue = remainingQty * livePrice;
        const livePL = currentValue - totalInvestment;
        const livePLPercent = totalInvestment ? (livePL / totalInvestment) * 100 : 0;

        return createPortal(
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2500] p-4">
                <div className="bg-white rounded-lg shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
                        <div className="flex items-center gap-3">
                            <Activity size={24} className="text-blue-600" />
                            <h3 className="text-xl font-semibold text-gray-900">Live Price Analytics - {stockName}</h3>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                                onClick={() => {
                                    setLivePriceModal(false);
                                    setLivePriceData(null);
                                    setPriceError(null);
                                }}
                            className="h-8 w-8 p-0 hover:bg-red-100 hover:scale-110 transition-all duration-200 bg-red-50 border border-red-200 text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                        {loadingPrice ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <RefreshCw size={24} className="animate-spin mb-3" />
                                <span>Fetching live price data...</span>
                            </div>
                        ) : priceError ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
                                    <AlertCircle size={24} className="text-red-600 mx-auto mb-3" />
                                    <p className="text-red-800 mb-4">{priceError}</p>
                                    <Button
                                        variant="outline"
                                        onClick={() => stockName && fetchLivePrice(stockName)}
                                        className="border-red-300 text-red-700 hover:bg-red-100"
                                    >
                                        <RefreshCw size={16} className="mr-2" />
                                        Retry
                                    </Button>
                                </div>
                            </div>
                        ) : livePriceData ? (
                            <div className="space-y-8">
                                {/* Market Price Section */}
                                <div className="bg-gray-50 rounded-lg p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <TrendingUp size={20} className="text-green-600" />
                                        <h4 className="text-lg font-semibold text-gray-900">Market Price</h4>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center gap-2 mb-2">
                                                <DollarSign size={16} className="text-blue-600" />
                                                <span className="text-sm font-medium text-gray-600">Last Price</span>
                                            </div>
                                            <div className={`text-2xl font-bold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatAmount(parseFloat(livePriceData.ltp))}
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center gap-2 mb-2">
                                                {change >= 0 ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
                                                <span className="text-sm font-medium text-gray-600">Change</span>
                                            </div>
                                            <div className={`text-2xl font-bold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatPercentage(change)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Position Summary Section */}
                                {currentStock && currentDetails && currentStock.remainingQty > 0 && (
                                    <div className="bg-blue-50 rounded-lg p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <BarChart3 size={20} className="text-blue-600" />
                                            <h4 className="text-lg font-semibold text-gray-900">Your Position</h4>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Package size={16} className="text-purple-600" />
                                                    <span className="text-sm font-medium text-gray-600">Holding Qty</span>
                                                </div>
                                                <div className="text-xl font-bold text-gray-900">{formatNumber(currentStock.remainingQty)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <DollarSign size={16} className="text-blue-600" />
                                                    <span className="text-sm font-medium text-gray-600">Avg. Cost</span>
                                                </div>
                                                <div className="text-xl font-bold text-gray-900">{formatAmount(currentDetails.avgCost)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <DollarSign size={16} className="text-indigo-600" />
                                                    <span className="text-sm font-medium text-gray-600">Investment</span>
                                                </div>
                                                <div className="text-xl font-bold text-gray-900">{formatAmount(currentStock.totalInvestment)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <DollarSign size={16} className="text-emerald-600" />
                                                    <span className="text-sm font-medium text-gray-600">Current Value</span>
                                                </div>
                                                <div className="text-xl font-bold text-gray-900">{formatAmount(currentDetails.currentValue)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    {currentDetails.liveProfitLoss >= 0 ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
                                                    <span className="text-sm font-medium text-gray-600">Live P/L</span>
                                                </div>
                                                <div className={`text-xl font-bold ${currentDetails.liveProfitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatAmount(currentDetails.liveProfitLoss)}
                                                    <div className="text-sm font-normal mt-1">
                                                        ({formatPercentage(currentDetails.liveProfitLossPercentage)})
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Analytics Dashboard Section */}
                                {currentStock && (
                                    <div className="bg-purple-50 rounded-lg p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <BarChart3 size={20} className="text-purple-600" />
                                            <h4 className="text-lg font-semibold text-gray-900">Analytics Dashboard</h4>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Package size={16} className="text-blue-600" />
                                                    <span className="text-sm font-medium text-gray-600">Stock Name</span>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">{stockName}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Package size={16} className="text-purple-600" />
                                                    <span className="text-sm font-medium text-gray-600">Remaining Qty</span>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">{formatNumber(remainingQty)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <DollarSign size={16} className="text-green-600" />
                                                    <span className="text-sm font-medium text-gray-600">Avg. Buy Price</span>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">{formatAmount(avgBuy)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <DollarSign size={16} className="text-blue-600" />
                                                    <span className="text-sm font-medium text-gray-600">Investment</span>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">{formatAmount(totalInvestment)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <DollarSign size={16} className="text-emerald-600" />
                                                    <span className="text-sm font-medium text-gray-600">Current Value</span>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">{formatAmount(currentValue)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    {livePL >= 0 ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
                                                    <span className="text-sm font-medium text-gray-600">Live P/L</span>
                                                </div>
                                                <div className={`text-lg font-bold ${livePL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatAmount(livePL)}
                                                    <div className="text-sm font-normal mt-1">({formatPercentage(livePLPercent)})</div>
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <DollarSign size={16} className="text-indigo-600" />
                                                    <span className="text-sm font-medium text-gray-600">Total Returns</span>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">{formatAmount(totalReturns)}</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    {realizedPL >= 0 ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
                                                    <span className="text-sm font-medium text-gray-600">Realized P/L</span>
                                                </div>
                                                <div className={`text-lg font-bold ${realizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatAmount(realizedPL)}
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Target size={16} className="text-orange-600" />
                                                    <span className="text-sm font-medium text-gray-600">Watch Price</span>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">
                                                    {watchPrice ? formatAmount(watchPrice) : '—'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Transaction History Section */}
                                {currentStock && (
                                    <div className="bg-yellow-50 rounded-lg p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Calendar size={20} className="text-yellow-600" />
                                            <h4 className="text-lg font-semibold text-gray-900">Transaction History</h4>
                                        </div>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <TrendingUp size={16} className="text-green-600" />
                                                    <span className="text-lg font-semibold text-gray-900">Buy History</span>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full">
                                                        <thead>
                                                            <tr className="border-b border-gray-200">
                                                                <th className="text-left py-2 px-2 text-sm font-semibold text-gray-700">Price</th>
                                                                <th className="text-left py-2 px-2 text-sm font-semibold text-gray-700">Qty</th>
                                                                <th className="text-left py-2 px-2 text-sm font-semibold text-gray-700">Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {buyHistory.length ? buyHistory.map((b, i) => (
                                                                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                                                    <td className="py-2 px-2 text-sm text-gray-900">{formatAmount(b.price)}</td>
                                                                    <td className="py-2 px-2 text-sm text-gray-900">{formatNumber(b.quantity)}</td>
                                                                    <td className="py-2 px-2 text-sm text-gray-900">{(() => {
                                                                        if (!b.date) return 'N/A';
                                                                        try {
                                                                            // Check if the original date string contains time information
                                                                            const hasTime = b.date.includes('T') && /\d{1,2}:\d{2}/.test(b.date.split('T')[1] || '');
                                                                            const date = new Date(b.date);
                                                                            if (isNaN(date.getTime())) return 'N/A';

                                                                            if (hasTime) {
                                                                                return date.toLocaleString('en-IN', {
                                                                                    timeZone: 'Asia/Kolkata',
                                                                                    year: 'numeric',
                                                                                    month: '2-digit',
                                                                                    day: '2-digit',
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                    second: '2-digit',
                                                                                    hour12: true
                                                                                });
                                                                            } else {
                                                                                return date.toLocaleDateString('en-IN', {
                                                                                    timeZone: 'Asia/Kolkata',
                                                                                    year: 'numeric',
                                                                                    month: '2-digit',
                                                                                    day: '2-digit'
                                                                                });
                                                                            }
                                                                        } catch (error) {
                                                                            return 'N/A';
                                                                        }
                                                                    })()}</td>
                                                                </tr>
                                                            )) : <tr><td colSpan={3} className="py-4 px-2 text-center text-gray-500 text-sm">—</td></tr>}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <TrendingDown size={16} className="text-red-600" />
                                                    <span className="text-lg font-semibold text-gray-900">Sell History</span>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full">
                                                        <thead>
                                                            <tr className="border-b border-gray-200">
                                                                <th className="text-left py-2 px-2 text-sm font-semibold text-gray-700">Price</th>
                                                                <th className="text-left py-2 px-2 text-sm font-semibold text-gray-700">Qty</th>
                                                                <th className="text-left py-2 px-2 text-sm font-semibold text-gray-700">Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sellHistory.length ? sellHistory.map((s, i) => (
                                                                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                                                    <td className="py-2 px-2 text-sm text-gray-900">{formatAmount(s.price)}</td>
                                                                    <td className="py-2 px-2 text-sm text-gray-900">{formatNumber(s.quantity)}</td>
                                                                    <td className="py-2 px-2 text-sm text-gray-900">{(() => {
                                                                        if (!s.date) return 'N/A';
                                                                        try {
                                                                            // Check if the original date string contains time information
                                                                            const hasTime = s.date.includes('T') && /T\d{1,2}:\d{2}(:\d{2})?(\+|-|\d{2}:\d{2})?/.test(s.date);
                                                                            const date = new Date(s.date);
                                                                            if (isNaN(date.getTime())) return 'N/A';

                                                                            if (hasTime) {
                                                                                return date.toLocaleString('en-IN', {
                                                                                    timeZone: 'Asia/Kolkata',
                                                                                    year: 'numeric',
                                                                                    month: '2-digit',
                                                                                    day: '2-digit',
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                    second: '2-digit',
                                                                                    hour12: true
                                                                                });
                                                                            } else {
                                                                                return date.toLocaleDateString('en-IN', {
                                                                                    timeZone: 'Asia/Kolkata',
                                                                                    year: 'numeric',
                                                                                    month: '2-digit',
                                                                                    day: '2-digit'
                                                                                });
                                                                            }
                                                                        } catch (error) {
                                                                            return 'N/A';
                                                                        }
                                                                    })()}</td>
                                                                </tr>
                                                            )) : <tr><td colSpan={3} className="py-4 px-2 text-center text-gray-500 text-sm">—</td></tr>}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Last Updated Footer */}
                                <div className="flex items-center gap-2 mt-6 pt-4 border-t border-gray-200 text-gray-500">
                                    <Calendar size={14} />
                                    <span className="text-sm">Last Updated: {(() => {
                                        // Debug: Log the timestamp to see its format
                                        console.log('Timestamp from backend:', livePriceData.timestamp);
                                        console.log('Timestamp type:', typeof livePriceData.timestamp);

                                        // Handle different timestamp formats
                                        if (!livePriceData.timestamp) return 'N/A';

                                        // If it's already a formatted string, return as is
                                        if (typeof livePriceData.timestamp === 'string' && livePriceData.timestamp.includes(' ')) {
                                            return livePriceData.timestamp;
                                        }

                                        // If it's a number (Unix timestamp), convert it
                                        if (typeof livePriceData.timestamp === 'number') {
                                            const date = new Date(livePriceData.timestamp * 1000);
                                            return date.toLocaleString('en-IN', {
                                                timeZone: 'Asia/Kolkata',
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                hour12: true
                                            });
                                        }

                                        // Try to parse as date string
                                        try {
                                            const date = new Date(livePriceData.timestamp);
                                            if (!isNaN(date.getTime())) {
                                                return date.toLocaleString('en-IN', {
                                                    timeZone: 'Asia/Kolkata',
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                    hour12: true
                                                });
                                            }
                                        } catch (error) {
                                            console.error('Error parsing timestamp:', error);
                                        }

                                        // Fallback to original function
                                        return formatIndianDateTime(livePriceData.timestamp);
                                    })()}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <AlertCircle size={24} className="mb-3" />
                                <span>No price data available</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    const DeleteConfirmationModal = () => {
        if (!deleteModalOpen || !stockToDelete) return null;

        return createPortal(
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[3000] p-4">
                <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
                    <div className="flex items-center gap-3 p-6 border-b border-gray-200">
                        <AlertCircle size={24} className="text-red-600" />
                        <h3 className="text-xl font-semibold text-gray-900">Delete Stock</h3>
                    </div>

                    <div className="p-6">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <p className="text-red-800 font-semibold mb-2"><strong>Warning:</strong> This action cannot be undone!</p>
                            <p className="text-red-700 mb-3">You are about to delete <strong>{stockToDelete.stockName}</strong> and all its associated data including:</p>
                            <ul className="list-disc list-inside text-red-700 space-y-1">
                                <li>All buy transactions</li>
                                <li>All sell transactions</li>
                                <li>Watch price settings</li>
                                <li>Investment history</li>
                            </ul>
                        </div>

                        <div className="space-y-3">
                            <p className="text-gray-700">To confirm deletion, please type: <strong className="text-red-600">{deleteConfirmationText}</strong></p>
                            <Input
                                type="text"
                                value={userInputText}
                                onChange={(e) => setUserInputText(e.target.value)}
                                placeholder={`Type "${deleteConfirmationText}" to confirm`}
                                className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
                        <Button
                            variant="outline"
                            onClick={handleCancelDelete}
                            disabled={isDeleting}
                            className="border-gray-300 text-gray-700 hover:bg-gray-100"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={userInputText !== deleteConfirmationText || isDeleting}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {isDeleting ? (
                                <>
                                    <RefreshCw size={16} className="mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={16} className="mr-2" />
                                    Delete Stock
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    return (
        <div style={{ 
            all: 'unset', 
            fontFamily: 'system-ui, -apple-system, sans-serif',
            color: '#111827',
            backgroundColor: 'transparent'
        }}>
            {isOpen && createPortal(
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1500] p-4">
                    <div className="bg-white rounded-lg shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
                            <div className="flex items-center gap-4">
                                <BarChart3 size={28} className="text-blue-600" />
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">{sector || 'Uncategorized'} Sector Details</h2>
                                    {sectorStats && (
                                        <div className="flex items-center gap-6 mt-2">
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <Package size={16} />
                                                <span className="text-sm font-medium">{sectorStats.totalStocks} Stocks</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <DollarSign size={16} />
                                                <span className="text-sm font-medium">{formatAmount(sectorStats.totalInvestment)}</span>
                                            </div>
                                            <div className={`flex items-center gap-2 text-sm font-medium ${getProfitLossClass(sectorStats.totalProfitLoss)}`}>
                                                {sectorStats.totalProfitLoss >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                                                <span>{formatAmount(sectorStats.totalProfitLoss)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {!loading && !error && stocks.length > 0 && (
                                    <Button
                                        variant="outline"
                                        onClick={handleDownload}
                                        className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
                                    >
                                        <Download size={16} />
                                        Export to CSV
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                        setStocks([]);
                                        onClose();
                                    }}
                                    className="h-8 w-8 p-0 hover:bg-red-100 hover:scale-110 transition-all duration-200 bg-red-50 border border-red-200 text-red-600 hover:text-red-700 hover:border-red-300"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(95vh-100px)]">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                    <RefreshCw size={24} className="animate-spin mb-3" />
                                    <span>Loading sector data...</span>
                                </div>
                            ) : error ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
                                        <AlertCircle size={24} className="text-red-600 mx-auto mb-3" />
                                        <p className="text-red-800 mb-4">{error}</p>
                                        <Button
                                            variant="outline"
                                            onClick={fetchSectorStocks}
                                            className="border-red-300 text-red-700 hover:bg-red-100"
                                        >
                                            <RefreshCw size={16} className="mr-2" />
                                            Retry
                                        </Button>
                                    </div>
                                </div>
                            ) : stocks.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                    <Package size={24} className="mb-3" />
                                    <span>No stocks found in {sector || 'Uncategorized'} sector</span>
                                </div>
                            ) : (
                                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Stock Name</th>
                                                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Buy Transactions</th>
                                                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Sell Transactions</th>
                                                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Remaining Qty</th>
                                                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Investment</th>
                                                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Returns</th>
                                                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">P/L</th>
                                                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Watch Price</th>
                                                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {stocks.map((stock) => (
                                                    <tr key={stock.id} className="hover:bg-gray-50">
                                                        <td className="py-4 px-4 text-sm font-medium text-gray-900">{stock.stockName}</td>
                                                        <td className="py-4 px-4">
                                                            {stock.buyDetails.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    {stock.buyDetails.map((buy, index) => (
                                                                        <div key={index}>
                                                                            {formatTransaction(buy)}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : <span className="text-gray-400 text-sm">No buy transactions</span>}
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            {stock.sellDetails.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    {stock.sellDetails.map((sell, index) => (
                                                                        <div key={index}>
                                                                            {formatTransaction(sell)}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : <span className="text-gray-400 text-sm">No sell transactions</span>}
                                                        </td>
                                                        <td className="py-4 px-4 text-center text-sm text-gray-900">{stock.remainingQty}</td>
                                                        <td className="py-4 px-4 text-right text-sm font-medium text-gray-900">{formatAmount(stock.totalInvestment)}</td>
                                                        <td className="py-4 px-4 text-right text-sm font-medium text-gray-900">{formatAmount(stock.totalReturns)}</td>
                                                        <td className={`py-4 px-4 text-right text-sm font-medium ${getProfitLossClass(stock.profitLoss)}`}>
                                                            {formatAmount(stock.profitLoss)}
                                                        </td>
                                                        <td className="py-4 px-4 text-center text-sm text-gray-900">
                                                            {stock.watchDetails?.targetPrice ?
                                                                formatAmount(stock.watchDetails.targetPrice) :
                                                                '—'}
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <Button
                                                                    variant="outline"
                                                                    size="icon"
                                                                    onClick={() => handleEdit(stock.id)}
                                                                    className="h-8 w-8 border-blue-300 text-blue-600 hover:bg-blue-50"
                                                                    title={`Edit ${stock.stockName}`}
                                                                >
                                                                    <Edit2 size={16} />
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="icon"
                                                                    onClick={() => handleViewDetails(stock.id, stock.stockName)}
                                                                    className="h-8 w-8 border-green-300 text-green-600 hover:bg-green-50"
                                                                    title={`View Live Price for ${stock.stockName}`}
                                                                >
                                                                    <Eye size={16} />
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="icon"
                                                                    onClick={() => handleDelete(stock)}
                                                                    className="h-8 w-8 border-red-300 text-red-600 hover:bg-red-50"
                                                                    title={`Delete ${stock.stockName}`}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <LivePriceModal />
            <DeleteConfirmationModal />
            {isEditFormOpen && createPortal(
                <StockEditForm
                    isOpen={isEditFormOpen}
                    onClose={handleEditFormClose}
                    onSave={handleEditFormSave}
                    stockId={selectedStockId}
                />,
                document.body
            )}
        </div>
    );
};

export default SectorDetailsDialog;
